/**
 * `threadProjection` (slice 1 spec §7.2, contracts §6.5): the thread a view
 * renders, from the loaded thread plus the live `thread.token` blocks that
 * arrived since. Pure, so both surfaces (Chats and the side panel) share it,
 * and so it can be tested without a socket. The idea is ported from
 * `@openclaw/gateway-client`'s session projection, not imported.
 *
 * - Blocks append per `(turn_id, part)` in `seq` order.
 * - A block at or below what's rendered for its part (the loaded message's
 *   `stream_seq`, then the live blocks after it) is dropped: a late tab or a
 *   reconnect never shows a block twice.
 * - A gap in `seq` marks the projection `stale`: the caller refetches. So does
 *   a first block past `seq` 1 for a part it has never seen.
 * - A refetch (`loaded`) replaces the thread and drops the live blocks the
 *   refetched messages now cover, and every block of a turn it shows finished:
 *   a finished turn's blocks are all stored, unless the Worker removed them
 *   (an auth failure is never a reply, slice 1b spec §5).
 * - Slice 1q §6.1: a running pi turn's live draft (`thread.draft`), its
 *   Working state (`turn.live`) and the reply so far (`/live`) are kept per
 *   turn. Its part 0 shows as the draft when that's longer than what's stored.
 */
import type { ThreadDetail, ThreadMessage } from "./api";

/** One finished block, as `thread.token` carries it. */
export type Block = { turn_id: string; part: number; seq: number; text: string };

type LivePart = { turn_id: string; part: number; blocks: Array<{ seq: number; text: string }> };

/** What a running turn is doing (slice 1q §2.2). */
export type LiveState = "thinking" | "writing" | "working" | "retrying";

/** `turn.live`'s state. */
export type TurnLive = { turn_id: string; state: LiveState; attempt?: number; of?: number };

/** A running turn's reply as it shows (slice 1q §2.1), with its Working state. */
export type Draft = {
  turn_id: string;
  gen: number;
  text: string;
  live: TurnLive | null;
};

/** `thread.draft`: `text` at offset `at` of draft `gen`. */
export type DraftPiece = { turn_id: string; gen: number; at: number; text: string };

/** `GET /api/threads/:id/live`'s answer. */
export type SoFar = { turn_id: string; gen: number; text: string; state: LiveState | null } | null;

export type Projection = {
  loaded: ThreadDetail | null;
  /** Blocks past the loaded messages, per part, sorted by `seq`. */
  live: LivePart[];
  /** A gap was seen: refetch the thread. A `loaded` that covers it clears this. */
  stale: boolean;
  /** Each running turn's draft (slice 1q). */
  drafts: Draft[];
  /** A draft had a hole: fetch the reply so far. A `so_far` clears this. */
  staleLive: boolean;
};

export type ProjectionAction =
  | { type: "loaded"; thread: ThreadDetail }
  | { type: "block"; block: Block }
  | { type: "draft"; draft: DraftPiece }
  | { type: "live"; live: TurnLive }
  | { type: "so_far"; live: SoFar };

export const emptyProjection: Projection = {
  loaded: null,
  live: [],
  stale: false,
  drafts: [],
  staleLive: false,
};

const samePart = (
  a: { turn_id: string | null; part: number | null },
  turnId: string,
  part: number,
) => a.turn_id === turnId && a.part === part;

function loadedMessage(loaded: ThreadDetail | null, turnId: string, part: number) {
  return loaded?.messages.find((m) => m.kind === "text" && samePart(m, turnId, part));
}

/** The last `seq` the loaded thread has for a part (0 when it has none). */
function loadedSeq(loaded: ThreadDetail | null, turnId: string, part: number): number {
  return loadedMessage(loaded, turnId, part)?.stream_seq ?? 0;
}

/** The loaded thread shows the turn done or failed: it streams nothing more. */
function finished(loaded: ThreadDetail | null, turnId: string): boolean {
  const status = loaded?.turns.find((t) => t.id === turnId)?.status;
  return status === "done" || status === "failed";
}

/** The blocks that follow on from `base` without a hole, and the `seq` they reach. */
function contiguous(base: number, blocks: LivePart["blocks"]) {
  const run: LivePart["blocks"] = [];
  let last = base;
  for (const block of blocks) {
    if (block.seq !== last + 1) break;
    run.push(block);
    last = block.seq;
  }
  return { run, last };
}

function hasGap(loaded: ThreadDetail | null, live: LivePart[]): boolean {
  return live.some((p) => {
    const { run } = contiguous(loadedSeq(loaded, p.turn_id, p.part), p.blocks);
    return run.length < p.blocks.length;
  });
}

/** The state with `turnId`'s draft replaced (or added). */
function withDraft(state: Projection, draft: Draft, staleLive = state.staleLive): Projection {
  const drafts = state.drafts.some((d) => d.turn_id === draft.turn_id)
    ? state.drafts.map((d) => (d.turn_id === draft.turn_id ? draft : d))
    : [...state.drafts, draft];
  return { ...state, drafts, staleLive };
}

/** Slice 1q §6.1: `/live`'s answer replaces a turn's draft when it's newer, and clears a hole. */
/** Whether the reply so far is ahead of the draft a tab has. */
const ahead = (so: NonNullable<SoFar>, had: Draft | undefined) =>
  !had || so.gen > had.gen || (so.gen === had.gen && so.text.length > had.text.length);

/** The Working state: the one the events brought, as new as the answer's or newer, else the answer's. */
const stateOf = (so: NonNullable<SoFar>, had: Draft | undefined): TurnLive | null =>
  had?.live ?? (so.state ? { turn_id: so.turn_id, state: so.state } : null);

function soFarProjection(state: Projection, so: SoFar): Projection {
  if (!so || finished(state.loaded, so.turn_id)) return { ...state, staleLive: false };
  const had = state.drafts.find((d) => d.turn_id === so.turn_id);
  if (!ahead(so, had)) return { ...state, staleLive: false };
  const draft = { turn_id: so.turn_id, gen: so.gen, text: so.text, live: stateOf(so, had) };
  return withDraft(state, draft, false);
}

/** A `thread.draft` piece: appended at its offset, a new `gen` whole, a hole marked. */
function pieceProjection(state: Projection, piece: DraftPiece): Projection {
  const had = state.drafts.find((d) => d.turn_id === piece.turn_id);
  const base = had ?? { turn_id: piece.turn_id, gen: 0, text: "", live: null };
  const { gen, at, text } = piece;
  if (gen < base.gen) return state;
  // A new draft comes whole; a piece past its start is a hole.
  if (gen > base.gen) return at === 0 ? withDraft(state, { ...base, gen, text }) : hole(state);
  if (at > base.text.length) return hole(state);
  if (at + text.length <= base.text.length) return state;
  return withDraft(state, { ...base, text: base.text.slice(0, at) + text });
}

const hole = (state: Projection): Projection => ({ ...state, staleLive: true });

/** Slice 1q §6.1: a draft's piece, a turn's state, or the reply so far. */
function draftProjection(
  state: Projection,
  action: Extract<ProjectionAction, { type: "draft" | "live" | "so_far" }>,
): Projection {
  if (action.type === "so_far") return soFarProjection(state, action.live);
  const turnId = action.type === "draft" ? action.draft.turn_id : action.live.turn_id;
  if (finished(state.loaded, turnId)) return state;
  if (action.type === "draft") return pieceProjection(state, action.draft);
  const had = state.drafts.find((d) => d.turn_id === turnId);
  return withDraft(state, { turn_id: turnId, gen: 0, text: "", ...had, live: action.live });
}

export function threadProjection(state: Projection, action: ProjectionAction): Projection {
  if (action.type !== "loaded" && action.type !== "block") return draftProjection(state, action);
  if (action.type === "loaded") {
    if (action.thread === state.loaded) return state;
    const loaded = action.thread;
    const live = state.live
      .filter((p) => !finished(loaded, p.turn_id))
      .map((p) => {
        const seen = loadedSeq(loaded, p.turn_id, p.part);
        return { ...p, blocks: p.blocks.filter((b) => b.seq > seen) };
      })
      .filter((p) => p.blocks.length > 0);
    const drafts = state.drafts.filter((d) => !finished(loaded, d.turn_id));
    // A finished turn's hole goes with its draft; a running turn's next piece marks its own again.
    const staleLive = state.staleLive && drafts.length === state.drafts.length;
    return { ...state, loaded, live, stale: hasGap(loaded, live), drafts, staleLive };
  }

  const { turn_id, part, seq, text } = action.block;
  if (finished(state.loaded, turn_id)) return state;
  const base = loadedSeq(state.loaded, turn_id, part);
  const existing = state.live.find((p) => samePart(p, turn_id, part));
  const blocks = existing?.blocks ?? [];
  if (seq <= base || blocks.some((b) => b.seq === seq)) return state;

  const next = [...blocks, { seq, text }].sort((a, b) => a.seq - b.seq);
  const live = existing
    ? state.live.map((p) => (p === existing ? { ...p, blocks: next } : p))
    : [...state.live, { turn_id, part, blocks: next }];
  return { ...state, live, stale: hasGap(state.loaded, live) };
}

/** A message as a view renders it: the loaded one, or one still arriving block by block. */
export type ViewMessage = ThreadMessage & {
  /** Stable across a live message's first refetch: `turn:part` for replies. */
  key: string;
};

const viewKey = (m: ThreadMessage) =>
  m.kind === "text" && m.turn_id !== null && m.part !== null ? `${m.turn_id}:${m.part}` : m.id;

/**
 * The messages to render, oldest first: each loaded message with its part's
 * live blocks appended, then the parts that exist only as live blocks so far.
 * Only blocks that follow on without a hole are shown; the refetch fills holes.
 */
export function projectedMessages(state: Projection): ViewMessage[] {
  const { loaded, live, drafts } = state;
  const used = new Set<LivePart>();
  const drafted = new Set<string>();
  // Slice 1q: part 0 shows as its turn's draft when that's longer than what's stored.
  const shown = (turnId: string, part: number, body: string) => {
    const draft = part === 0 ? drafts.find((d) => d.turn_id === turnId) : undefined;
    if (!draft) return body;
    drafted.add(turnId);
    return draft.text.length > body.length ? draft.text : body;
  };
  const out: ViewMessage[] = (loaded?.messages ?? []).map((m) => {
    const key = viewKey(m);
    if (m.kind !== "text" || m.turn_id === null || m.part === null) return { ...m, key };
    const turnId = m.turn_id;
    const part = m.part;
    const p = live.find((l) => samePart(l, turnId, part));
    if (!p) return { ...m, key, body: shown(turnId, part, m.body) };
    used.add(p);
    const { run, last } = contiguous(m.stream_seq ?? 0, p.blocks);
    const body = shown(turnId, part, m.body + run.map((b) => b.text).join(""));
    return { ...m, key, body, stream_seq: run.length > 0 ? last : m.stream_seq };
  });
  const liveMessage = (turnId: string, part: number, body: string, seq: number | null) => {
    const turn = loaded?.turns.find((t) => t.id === turnId);
    return {
      id: `live:${turnId}:${part}`,
      key: `${turnId}:${part}`,
      thread_id: loaded?.thread.id ?? "",
      author_user_id: null,
      author_agent_id: turn?.agent_id ?? null,
      kind: "text",
      body,
      data: {},
      turn_id: turnId,
      part,
      stream_seq: seq,
      created_at: turn?.started_at ?? turn?.created_at ?? loaded?.thread.last_message_at ?? "",
    } satisfies ViewMessage;
  };
  for (const p of live) {
    if (used.has(p)) continue;
    const { run, last } = contiguous(0, p.blocks);
    if (run.length === 0) continue;
    const body = shown(p.turn_id, p.part, run.map((b) => b.text).join(""));
    out.push(liveMessage(p.turn_id, p.part, body, last));
  }
  for (const d of drafts) {
    if (drafted.has(d.turn_id) || d.text === "") continue;
    out.push(liveMessage(d.turn_id, 0, d.text, null));
  }
  return out;
}

/** A running turn's Working state from its live events (slice 1q §6.3), or null. */
export function liveStateOf(state: Projection, turnId: string): TurnLive | null {
  return state.drafts.find((d) => d.turn_id === turnId)?.live ?? null;
}
